// ProducersStep.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Tabs,
  Tab,
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  Paper,
  Tooltip,
  Alert,
  Checkbox,
  FormControlLabel,
  FormHelperText
} from '@mui/material';
import PhoneMaskCustom from '../../components/PhoneMaskCustom';
import STATES from '../../constants/states';
import { isValidEmail, isValidPhone, isValidTaxId, isValidZipCode } from '../../utils/validators';
import OwnerTabs from './OwnerTabs';
import { SignupData, Producer, Owner } from 'form-application-shared';

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

// Helper component for the producer tab panels
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`producer-tabpanel-${index}`}
      aria-labelledby={`producer-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

interface ProducersStepProps {
  formData: SignupData;
  setFormData: React.Dispatch<React.SetStateAction<SignupData>>;
  onValidationChange: (validationFn: () => boolean) => void;
}

// Type for validation errors structure
interface FieldErrors {
  [producerIndex: number]: {
    [field: string]: boolean | any;
    owners?: {
      [ownerIndex: number]: {
        [field: string]: boolean;
      }
    }
  }
}

const ProducersStep: React.FC<ProducersStepProps> = ({ formData, setFormData, onValidationChange }) => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [validationMode, setValidationMode] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [generalErrors, setGeneralErrors] = useState<string[]>([]);
  const formDataRef = useRef<SignupData>(formData);

  // Keep the ref updated with latest formData
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // Helper function to check if owner has any data entered
  const ownerHasData = useCallback((owner: Owner): boolean => {
    return Boolean(
      owner.legalName || 
      owner.email || 
      owner.taxId || 
      owner.addressLines || 
      owner.city || 
      owner.state || 
      owner.zip || 
      (owner.percentOwned && owner.percentOwned !== 0) || 
      owner.familyRelation
    );
  }, []);

  // Helper function to check if owner is complete
  const validateOwner = useCallback((owner: Owner) => {
    const errors: Record<string, boolean> = {};
    
    // Only validate if owner has any data
    if (ownerHasData(owner)) {
      if (!owner.legalName) errors.legalName = true;
      if (!owner.taxId) errors.taxId = true;
      if (!owner.addressLines) errors.addressLines = true;
      if (!owner.city) errors.city = true;
      if (!owner.state) errors.state = true;
      if (!owner.zip) errors.zip = true;
      if (!owner.familyRelation) errors.familyRelation = true;
      
      // Validate formats if provided
      if (owner.email && !isValidEmail(owner.email)) {
        errors.email = true;
      }
      if (owner.taxId && !isValidTaxId(owner.taxId)) {
        errors.taxId = true;
      }
      if (owner.zip && !isValidZipCode(owner.zip)) {
        errors.zip = true;
      }
    }
    
    return {
      errors,
      hasErrors: Object.keys(errors).length > 0,
      isEmpty: !ownerHasData(owner)
    };
  }, [ownerHasData]);

  // Helper function to check if producer has any data entered
  const producerHasData = useCallback((producer: Producer): boolean => {
    // Check if any direct producer fields have data
    const hasDirectData = Boolean(
      producer.legalName || 
      producer.relationship || 
      producer.otherRelationship ||
      producer.contact || 
      producer.role || 
      producer.email || 
      producer.addressLines || 
      producer.city || 
      producer.state || 
      producer.zip || 
      producer.telephone || 
      producer.fax || // Keep checking fax for data even though it's optional
      producer.taxId || 
      producer.taxType
    );
    
    // Check if any owner has data
    const hasOwnerData = producer.owners?.some(ownerHasData) || false;
    
    return hasDirectData || hasOwnerData;
  }, [ownerHasData]);

  // Validate a producer and its owners
  const validateProducer = useCallback((producer: Producer) => {
    const errors: Record<string, any> = {};
    
    // Only validate if producer has any data
    if (producerHasData(producer)) {
      if (!producer.legalName) errors.legalName = true;
      if (!producer.relationship) errors.relationship = true;
      // If relationship is "Other", require otherRelationship
      if (producer.relationship === 'Other' && !producer.otherRelationship) {
        errors.otherRelationship = true;
      }
      if (!producer.contact) errors.contact = true;
      if (!producer.role) errors.role = true;
      if (!producer.addressLines) errors.addressLines = true;
      if (!producer.city) errors.city = true;
      if (!producer.state) errors.state = true;
      if (!producer.zip) errors.zip = true;
      if (!producer.telephone) errors.telephone = true;
      // Fax is optional - don't require it
      if (!producer.taxId) errors.taxId = true;
      if (!producer.taxType) errors.taxType = true;
      
      // Validate formats if provided
      if (producer.email && !isValidEmail(producer.email)) {
        errors.email = true;
      }
      if (producer.taxId && !isValidTaxId(producer.taxId)) {
        errors.taxId = true;
      }
      if (producer.zip && !isValidZipCode(producer.zip)) {
        errors.zip = true;
      }
      if (producer.telephone && !isValidPhone(producer.telephone)) {
        errors.telephone = true;
      }
      if (producer.fax && !isValidPhone(producer.fax)) {
        errors.fax = true;
      }
      
      // Validate owners - make sure owners array exists
      if (!producer.owners || producer.owners.length === 0) {
        // Explicitly add an error if no owners exist
        errors.noOwners = true;
      } else {
        const ownerResults = producer.owners.map(validateOwner);
        const ownersErrors = producer.owners.map(owner => validateOwner(owner).errors);
        
        // Check if we have at least one complete owner
        const hasCompleteOwner = ownerResults.some(result => !result.hasErrors && !result.isEmpty);
        const hasPartialOwner = ownerResults.some(result => result.hasErrors && !result.isEmpty);
        
        // Always store owner errors if any owner has errors
        if (hasPartialOwner) {
          errors.owners = ownersErrors;
        }
        
        // If no complete owner, report error (even if no owner data is present)
        if (!hasCompleteOwner) {
          if (!errors.owners) {
            errors.owners = ownersErrors;
          }
          // Add special flag to indicate missing complete owner
          errors.missingCompleteOwner = true;
        }
      }
    }
    
    return {
      errors,
      hasErrors: Object.keys(errors).length > 0,
      isEmpty: !producerHasData(producer),
      hasPartialOwner: producer.owners?.some(owner => ownerHasData(owner) && validateOwner(owner).hasErrors) || false,
      hasCompleteOwner: producer.owners?.some(owner => !validateOwner(owner).hasErrors && !validateOwner(owner).isEmpty) || false
    };
  }, [producerHasData, validateOwner, ownerHasData]);
  
  // Check if CPI products are selected but no Finance Company relationship exists
  const checkCpiWithoutFinanceCompany = useCallback(() => {
    // Check if there's a CPI product in the products array
    const hasCpiProduct = formDataRef.current.products?.some(product => 
      product.toLowerCase().includes('cpi')
    ) || false;
    
    // Check if there's a producer with Finance Company relationship
    const hasFinanceCompany = formDataRef.current.producers.some(producer => 
      producer.relationship === 'Finance Company'
    );
    
    // Return true if we have CPI product but no Finance Company
    return hasCpiProduct && !hasFinanceCompany;
  }, []);

  // Full form validation
  const validateForm = useCallback(() => {
    // Enable validation mode to show field errors
    setValidationMode(true);
    
    const errors: FieldErrors = {};
    const generalErrorMessages: string[] = [];
    
    // Validate each producer
    formDataRef.current.producers.forEach((producer, idx) => {
      const result = validateProducer(producer);
      
      // Store field errors for this producer
      if (result.hasErrors && !result.isEmpty) {
        errors[idx] = result.errors;
      }
      
      // Add error message if producer is missing complete owner
      if (!result.isEmpty && !result.hasCompleteOwner) {
        generalErrorMessages.push(
          `Producer ${idx + 1} (${producer.legalName || 'Unnamed'}): At least one complete owner is required.`
        );
      }
      
      // Check for partially filled out owners
      if (result.hasPartialOwner) {
        generalErrorMessages.push(
          `Producer ${idx + 1} (${producer.legalName || 'Unnamed'}): One or more owners are partially filled out. Please complete all required fields.`
        );
      }
      
      // Validate that owner percentages add up to 100%
      if (!result.isEmpty && producer.owners?.some(ownerHasData)) {
        const totalPercentage = producer.owners.reduce((sum, owner) => {
          const percent = parseFloat(owner.percentOwned.toString()) || 0;
          return sum + percent;
        }, 0);
        
        // Check if total is not 100% (allowing for small floating point errors)
        if (Math.abs(totalPercentage - 100) > 0.01) {
          generalErrorMessages.push(
            `Producer ${idx + 1} (${producer.legalName || 'Unnamed'}): Owner percentages total ${totalPercentage.toFixed(2)}% instead of 100%`
          );
        }
      }
    });
    
    // Check if we have any valid producer with at least one valid owner
    const producerResults = formDataRef.current.producers.map(validateProducer);
    const hasValidProducerWithOwner = producerResults.some(result => 
      !result.hasErrors && !result.isEmpty && result.hasCompleteOwner
    );
    
    if (!hasValidProducerWithOwner) {
      generalErrorMessages.push("At least one producer with at least one complete owner must be filled out");
    }
    
    // Check if we need the finance company acknowledgment
    const needsFinanceCompanyAck = checkCpiWithoutFinanceCompany();
    
    // If we need acknowledgment but it's not checked, show error
    if (needsFinanceCompanyAck && !formDataRef.current.noFinanceCompanyAck) {
      generalErrorMessages.push("You must acknowledge the CPI product without a Finance Company statement to continue");
    }
    
    // Store validation results
    setFieldErrors(errors);
    setGeneralErrors(generalErrorMessages);
    
    // Scroll to top if there are any errors
    if (Object.keys(errors).length > 0 || generalErrorMessages.length > 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    
    // Form is valid if no errors and at least one valid producer with owner
    // AND if finance company acknowledgment is checked when needed
    const isValid = Object.keys(errors).length === 0 && 
                   generalErrorMessages.length === 0 && 
                   hasValidProducerWithOwner &&
                   (!needsFinanceCompanyAck || formDataRef.current.noFinanceCompanyAck);
    
    return isValid;
  }, [validateProducer, ownerHasData, checkCpiWithoutFinanceCompany]);
  
  // Create a ref to the validateForm function that can be accessed by the parent component
  const validateFormRef = useRef<() => boolean>(validateForm);
  
  // Update ref on each render to always point to latest validateForm
  useEffect(() => {
    validateFormRef.current = validateForm;
  }, [validateForm]);
  
  // Expose validation method to parent through props
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(validateFormRef.current);
    }
    return () => {
      if (onValidationChange) {
        onValidationChange(() => true); // Default to true when unmounting
      }
    };
  }, [onValidationChange]);

  useEffect(() => {
    if (activeTab >= formData.producers.length && formData.producers.length > 0) {
      setActiveTab(formData.producers.length - 1);
    }
  }, [formData.producers.length, activeTab]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    if (newValue === formData.producers.length) {
      // "Add Producer" tab clicked: add new producer
      addProducer();
      setActiveTab(newValue);
    } else {
      setActiveTab(newValue);
    }
  };

  // Get error state for a producer field
  const getProducerFieldError = (producerIndex: number, fieldName: string): boolean => {
    if (!validationMode) return false;
    return !!fieldErrors?.[producerIndex]?.[fieldName];
  };

  // Get error message for a producer field
  const getProducerErrorMessage = (producerIndex: number, fieldName: string): string => {
    if (!validationMode) return "";
    
    const producer = formData.producers[producerIndex];
    
    if (fieldName === 'email' && getProducerFieldError(producerIndex, fieldName)) {
      return "Invalid email format";
    }
    if (fieldName === 'taxId' && producer.taxId && !isValidTaxId(producer.taxId)) {
      return "Invalid Tax ID format (use SSN: XXX-XX-XXXX or EIN: XX-XXXXXXX)";
    }
    if (fieldName === 'zip' && producer.zip && !isValidZipCode(producer.zip)) {
      return "Invalid ZIP code format (use XXXXX or XXXXX-XXXX)";
    }
    if (fieldName === 'telephone' && producer.telephone && !isValidPhone(producer.telephone)) {
      return "Invalid phone format (10 digits required)";
    }
    if (fieldName === 'fax' && producer.fax && !isValidPhone(producer.fax)) {
      return "Invalid fax format (10 digits required)";
    }
    if (fieldName === 'otherRelationship' && getProducerFieldError(producerIndex, fieldName)) {
      return "Please specify the business type";
    }
    
    return getProducerFieldError(producerIndex, fieldName) ? `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required` : "";
  };

  // Check if a producer has any errors
  const producerHasError = (producerIndex: number): boolean => {
    if (!validationMode || !fieldErrors?.[producerIndex]) return false;
    
    // Check basic producer fields
    if (Object.keys(fieldErrors[producerIndex]).some(key => key !== 'owners' && fieldErrors[producerIndex][key])) {
      return true;
    }
    
    // Check owner fields - safely handle potential undefined values
    const ownerErrors = fieldErrors[producerIndex].owners;
    if (!ownerErrors) return false;
    
    // Convert owners errors to an array if it's not already
    const ownerErrorsArray = Array.isArray(ownerErrors) 
      ? ownerErrors 
      : Object.values(ownerErrors);
    
    // Check if any owner has errors
    return ownerErrorsArray.some(ownerError => 
      ownerError && Object.values(ownerError).some(error => !!error)
    );
  };

  const handleProducerChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Update form data directly
    const newFormData = {...formData};
    if (newFormData.producers[index]) {
      newFormData.producers[index] = {
        ...newFormData.producers[index],
        [name]: value
      };
      setFormData(newFormData);
    }
  };

  // Direct modification of a specific field
  const handleOwnerChange = (producerIndex: number, ownerIndex: number, fieldName: keyof Owner, value: string | number) => {
    // Get references with optional chaining
    const producer = formData.producers[producerIndex];
    const owners = producer?.owners;
    const existingOwner = owners?.[ownerIndex];
  
    // Only proceed if everything exists
    if (producer && owners && existingOwner) {
      // Create a new form data object
      const newFormData = {...formData};
      
      // Create a new owners array to avoid direct mutation
      const newOwners = [...owners];
      
      // Create a new owner object with the updated field
      const updatedOwner = {
        ...existingOwner,
        [fieldName]: value
      };
      
      // Replace the owner in the new array
      newOwners[ownerIndex] = updatedOwner;
      
      // Create a new producer with updated owners
      const newProducer = {
        ...producer,
        owners: newOwners
      };
      
      // Update the producers array
      newFormData.producers[producerIndex] = newProducer;
      
      // Set the updated form data
      setFormData(newFormData);
    }
  };

  const addProducer = () => {
    const newFormData = {...formData};
    newFormData.producers.push({
      legalName: "",
      relationship: "",
      otherRelationship: "",
      contact: "",
      role: "",
      email: "",
      addressLines: "",
      city: "",
      state: "",
      zip: "",
      telephone: "",
      fax: "",
      taxId: "",
      taxType: "",
      owners: [
        {
          legalName: "",
          email: "",
          taxId: "",
          addressLines: "",
          city: "",
          state: "",
          zip: "",
          percentOwned: 0,
          familyRelation: ""
        }
      ]
    });
    setFormData(newFormData);
  };

  const handleRemoveProducer = (index: number) => {
    if (formData.producers.length > 1) {
      const newFormData = {...formData};
      newFormData.producers.splice(index, 1);
      setFormData(newFormData);
      
      if (activeTab >= formData.producers.length - 1) {
        setActiveTab(formData.producers.length - 2);
      }
    }
  };
  
  // Handler for the finance company acknowledgment checkbox
  const handleNoFinanceCompanyAckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFormData = {...formData};
    newFormData.noFinanceCompanyAck = e.target.checked;
    setFormData(newFormData);
  };

  // Owner functions are passed to OwnerTabs:
  const addOwner = (producerIndex: number) => {
    // Get a reference to the producer with optional chaining
    const producer = formData.producers[producerIndex];
    const owners = producer?.owners;
    
    // Only proceed if producer and owners exist
    if (producer && owners) {
      // Create a new form data object
      const newFormData = {...formData};
      
      // Create a new owners array by copying the existing one
      const newOwners = [...owners, {
        legalName: "",
        email: "",
        taxId: "",
        addressLines: "",
        city: "",
        state: "",
        zip: "",
        percentOwned: 0,
        familyRelation: ""
      }];
      
      // Create a new producer with the updated owners array
      newFormData.producers[producerIndex] = {
        ...producer,
        owners: newOwners
      };
      
      // Update the form data
      setFormData(newFormData);
    }
  };

  const removeOwner = (producerIndex: number, ownerIndex: number) => {
    // Get a reference to the owners array with proper type checking
    const owners = formData.producers[producerIndex]?.owners;
    
    // Only proceed if owners exist and there's more than one
    if (owners && owners.length > 1) {
      const newFormData = {...formData};
      // Since we know owners exists, we can now safely create a copy and modify it
      newFormData.producers[producerIndex] = {
        ...newFormData.producers[producerIndex],
        owners: [...owners.slice(0, ownerIndex), ...owners.slice(ownerIndex + 1)]
      };
      
      setFormData(newFormData);
    }
  };

  // Render a single producer form including nested owner tabs
  const renderProducerForm = (index: number) => {
    const producer = formData.producers[index];
    return (
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} container justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Producer {index + 1}</Typography>
            <Tooltip title={formData.producers.length === 1 ? "At least one producer is required" : ""}>
              <span>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => handleRemoveProducer(index)}
                  disabled={formData.producers.length === 1}
                >
                  Delete Producer
                </Button>
              </span>
            </Tooltip>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              id={`producer-${index}-legalName`}
              label="Company Legal Name"
              name="legalName"
              value={producer.legalName || ''}
              onChange={(e) => handleProducerChange(index, e as React.ChangeEvent<HTMLInputElement>)}
              fullWidth
              error={getProducerFieldError(index, 'legalName')}
              helperText={getProducerErrorMessage(index, 'legalName')}
            />
          </Grid>
          <Grid item xs={12} sm={producer.relationship === 'Other' ? 3 : 6}>
            <TextField
              id={`producer-${index}-relationship`}
              select
              label="Business Type"
              name="relationship"
              value={producer.relationship || ''}
              onChange={(e) => handleProducerChange(index, e as React.ChangeEvent<HTMLInputElement>)}
              fullWidth
              error={getProducerFieldError(index, 'relationship')}
              helperText={getProducerErrorMessage(index, 'relationship')}
            >
              <MenuItem value="">
                <em>Select Business Type</em>
              </MenuItem>
              <MenuItem value="Dealership">Dealership</MenuItem>
              <MenuItem value="Finance Company">Finance Company</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </TextField>
          </Grid>
          {producer.relationship === 'Other' && (
            <Grid item xs={12} sm={3}>
              <TextField
                id={`producer-${index}-otherRelationship`}
                label="Specify Business Type"
                name="otherRelationship"
                value={producer.otherRelationship || ''}
                onChange={(e) => handleProducerChange(index, e as React.ChangeEvent<HTMLInputElement>)}
                fullWidth
                error={getProducerFieldError(index, 'otherRelationship')}
                helperText={getProducerErrorMessage(index, 'otherRelationship')}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField
              id={`producer-${index}-contact`}
              label="Contact"
              name="contact"
              value={producer.contact || ''}
              onChange={(e) => handleProducerChange(index, e as React.ChangeEvent<HTMLInputElement>)}
              fullWidth
              error={getProducerFieldError(index, 'contact')}
              helperText={getProducerErrorMessage(index, 'contact')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              id={`producer-${index}-role`}
              label="Role"
              name="role"
              value={producer.role || ''}
              onChange={(e) => handleProducerChange(index, e as React.ChangeEvent<HTMLInputElement>)}
              fullWidth
              error={getProducerFieldError(index, 'role')}
              helperText={getProducerErrorMessage(index, 'role')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id={`producer-${index}-email`}
              label="Email"
              name="email"
              type="email"
              value={producer.email || ''}
              onChange={(e) => handleProducerChange(index, e as React.ChangeEvent<HTMLInputElement>)}
              fullWidth
              error={getProducerFieldError(index, 'email')}
              helperText={getProducerErrorMessage(index, 'email')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id={`producer-${index}-addressLines`}
              label="Address"
              name="addressLines"
              value={producer.addressLines || ''}
              onChange={(e) => handleProducerChange(index, e as React.ChangeEvent<HTMLInputElement>)}
              fullWidth
              error={getProducerFieldError(index, 'addressLines')}
              helperText={getProducerErrorMessage(index, 'addressLines')}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              id={`producer-${index}-city`}
              label="City"
              name="city"
              value={producer.city || ''}
              onChange={(e) => handleProducerChange(index, e as React.ChangeEvent<HTMLInputElement>)}
              fullWidth
              error={getProducerFieldError(index, 'city')}
              helperText={getProducerErrorMessage(index, 'city')}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              id={`producer-${index}-state`}
              select
              label="State"
              name="state"
              value={producer.state || ''}
              onChange={(e) => handleProducerChange(index, e as React.ChangeEvent<HTMLInputElement>)}
              fullWidth
              error={getProducerFieldError(index, 'state')}
              helperText={getProducerErrorMessage(index, 'state')}
            >
              <MenuItem value="">
                <em>Select State</em>
              </MenuItem>
              {STATES.map(({ code, name }) => (
                <MenuItem key={code} value={code}>
                  {code} - {name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              id={`producer-${index}-zip`}
              label="Zip"
              name="zip"
              value={producer.zip || ''}
              onChange={(e) => handleProducerChange(index, e as React.ChangeEvent<HTMLInputElement>)}
              fullWidth
              error={getProducerFieldError(index, 'zip')}
              helperText={getProducerErrorMessage(index, 'zip')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              id={`producer-${index}-telephone`}
              label="Telephone"
              name="telephone"
              value={producer.telephone || ''}
              onChange={(e) => handleProducerChange(index, e as React.ChangeEvent<HTMLInputElement>)}
              fullWidth
              error={getProducerFieldError(index, 'telephone')}
              helperText={getProducerErrorMessage(index, 'telephone')}
              InputProps={{ 
                inputComponent: PhoneMaskCustom as any 
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              id={`producer-${index}-fax`}
              label="Fax (Optional)"
              name="fax"
              value={producer.fax || ''}
              onChange={(e) => handleProducerChange(index, e as React.ChangeEvent<HTMLInputElement>)}
              fullWidth
              error={getProducerFieldError(index, 'fax')}
              helperText={getProducerErrorMessage(index, 'fax')}
              InputProps={{ 
                inputComponent: PhoneMaskCustom as any 
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              id={`producer-${index}-taxId`}
              label="Tax ID"
              name="taxId"
              value={producer.taxId || ''}
              onChange={(e) => handleProducerChange(index, e as React.ChangeEvent<HTMLInputElement>)}
              fullWidth
              error={getProducerFieldError(index, 'taxId')}
              helperText={getProducerErrorMessage(index, 'taxId')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              id={`producer-${index}-taxType`}
              select
              label="Tax Type"
              name="taxType"
              value={producer.taxType || ''}
              onChange={(e) => handleProducerChange(index, e as React.ChangeEvent<HTMLInputElement>)}
              fullWidth
              error={getProducerFieldError(index, 'taxType')}
              helperText={getProducerErrorMessage(index, 'taxType')}
            >
              <MenuItem value="">
                <em>Select Tax Type</em>
              </MenuItem>
              <MenuItem value="Partnership">Partnership</MenuItem>
              <MenuItem value="S-Corp">S-Corp</MenuItem>
              <MenuItem value="C-Corp">C-Corp</MenuItem>
              <MenuItem value="Sole Proprietorship">Sole Proprietorship</MenuItem>
              <MenuItem value="Producer Owner">Producer Owner</MenuItem>
            </TextField>
          </Grid>
        </Grid>
        {/* Nested Owners Tabs */}
        {producer.owners && (
          <OwnerTabs
            producerIndex={index}
            owners={producer.owners}
            addOwner={addOwner}
            removeOwner={removeOwner}
            handleOwnerChange={handleOwnerChange}
            validationMode={validationMode}
            fieldErrors={fieldErrors}
          />
        )}
      </Paper>
    );
  };

  // Check if we need to show the finance company acknowledgment
  const showFinanceCompanyAck = checkCpiWithoutFinanceCompany();

  return (
    <>
      <Typography variant="h5" gutterBottom>
        Producers
      </Typography>

      <Box mt={2} mb={3}>
        <Typography variant="body2" color="textSecondary" fontWeight="bold">
          These are the entities that will be producing contracts. Please use all legal names.
        </Typography>
      </Box>

      {/* General validation error messages */}
      {validationMode && generalErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Please fix the following errors:
          </Typography>
          <ul>
            {generalErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Tabbed interface for producers */}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        {formData.producers.map((_, index) => (
          <Tab 
            key={index} 
            label={`Producer ${index + 1}`}
            sx={producerHasError(index) ? { 
              color: 'error.main',
              '&::after': {
                content: '"*"',
                color: 'error.main',
                marginLeft: '2px'
              }
            } : {}}
          />
        ))}
        <Tab label="Add Producer" />
      </Tabs>

      {formData.producers.map((_, index) => (
        <TabPanel value={activeTab} index={index} key={index}>
          {renderProducerForm(index)}
        </TabPanel>
      ))}

      {/* Finance Company Acknowledgment Section */}
      {showFinanceCompanyAck && (
        <Paper elevation={2} sx={{ p: 2, mt: 3, bgcolor: 'warning.light' }}>
          <Typography variant="subtitle1" fontWeight="bold" mb={1}>
            Important Notice Regarding CPI Products
          </Typography>
          <Typography variant="body1" mb={2}>
            You have selected a CPI product but have not listed a Finance Company as one of your producers. 
            I acknowledge that I am producing a CPI product without a Finance Company producer.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.noFinanceCompanyAck || false}
                onChange={handleNoFinanceCompanyAckChange}
                color="primary"
                name="noFinanceCompanyAck"
              />
            }
            label="I acknowledge that I am producing a CPI product without a Finance Company producer."
          />
          {validationMode && showFinanceCompanyAck && !formData.noFinanceCompanyAck && (
            <FormHelperText error>
              You must acknowledge this statement to continue
            </FormHelperText>
          )}
        </Paper>
      )}
    </>
  );
};

export default ProducersStep;